import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api';
import { PrimaryButton, SecondaryButton } from './FigmaComponents';

type AuthMode = 'LOGIN' | 'REGISTER';
type IdentifierType = 'EMAIL' | 'PHONE';
type AuthCapabilities = { email: boolean; phone: boolean };

type ChallengeResponse = { challengeId: string; expiresAt: string; retryAfterSeconds: number };

// Server states a code can never recover from. Resending onto them is refused,
// so the only honest next step is a brand new challenge rather than a Resend
// button that will keep failing.
const TERMINAL_CODES = new Set(['ATTEMPTS_EXHAUSTED', 'RESEND_EXHAUSTED', 'CONSUMED', 'EXPIRED', 'CHALLENGE_CHANGED', 'SUPERSEDED']);

function failureMessage(cause: unknown, fallback: string) {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

export function OtpAuth({ onAuthenticated }: { onAuthenticated: (token: string) => Promise<void> }) {
  const [mode, setMode] = useState<AuthMode>('LOGIN');
  const [identifierType, setIdentifierType] = useState<IdentifierType>('PHONE');
  const [capabilities, setCapabilities] = useState<AuthCapabilities | null>(null);
  const [capabilitiesError, setCapabilitiesError] = useState('');
  const [capabilitiesBusy, setCapabilitiesBusy] = useState(true);
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [terminal, setTerminal] = useState(false);
  const [code, setCode] = useState('');
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function loadCapabilities() {
    setCapabilitiesBusy(true);
    setCapabilitiesError('');
    try {
      const result = await api<AuthCapabilities>('/auth/capabilities');
      if (typeof result?.email !== 'boolean' || typeof result?.phone !== 'boolean' || (!result.email && !result.phone)) {
        throw new Error('No sign-in methods are available.');
      }
      setCapabilities({ email: result.email, phone: result.phone });
      setIdentifierType(current => (
        (current === 'EMAIL' && result.email) || (current === 'PHONE' && result.phone)
          ? current
          : result.email ? 'EMAIL' : 'PHONE'
      ));
    } catch {
      setCapabilities(null);
      setCapabilitiesError('Sign-in methods are unavailable right now. Check your connection and try again.');
    } finally {
      setCapabilitiesBusy(false);
    }
  }

  useEffect(() => { void loadCapabilities(); }, []);

  useEffect(() => {
    if (!challengeId) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [challengeId]);

  const resendSeconds = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const expirySeconds = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const destinationLabel = useMemo(() => identifierType === 'PHONE' ? 'phone number' : 'email address', [identifierType]);
  // Expiry is a terminal state too: the server refuses to resend onto an
  // expired challenge, so the countdown reaching zero must offer a new code.
  const spent = terminal || expirySeconds === 0;

  function resetChallenge() {
    setChallengeId(''); setExpiresAt(0); setResendAt(0); setCode(''); setError(''); setTerminal(false);
  }

  function acceptChallenge(result: ChallengeResponse) {
    const receivedAt = Date.now();
    setChallengeId(result.challengeId);
    setExpiresAt(new Date(result.expiresAt).getTime());
    setResendAt(receivedAt + result.retryAfterSeconds * 1000);
    setNow(receivedAt);
    setCode('');
    setTerminal(false);
  }

  async function startChallenge() {
    if (!capabilities || (identifierType === 'EMAIL' ? !capabilities.email : !capabilities.phone)) {
      throw new Error('This sign-in method is currently unavailable.');
    }
    return api<ChallengeResponse>('/auth/challenges', {
      method: 'POST',
      body: JSON.stringify({ identifier, type: identifierType, purpose: mode, ...(mode === 'REGISTER' ? { name } : {}) })
    });
  }

  async function requestCode(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      acceptChallenge(await startChallenge());
    } catch (cause) {
      setError(failureMessage(cause, 'Unable to request a code. Check your connection and try again.'));
    } finally { setBusy(false); }
  }

  async function requestNewCode() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      acceptChallenge(await startChallenge());
    } catch (cause) {
      setError(failureMessage(cause, 'Unable to request a code. Check your connection and try again.'));
    } finally { setBusy(false); }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await api<{ token: string }>(`/auth/challenges/${challengeId}/verify`, {
        method: 'POST', body: JSON.stringify({ code, purpose: mode })
      });
      await onAuthenticated(result.token);
    } catch (cause) {
      // Only reachable after the code was accepted, so this is guidance for a
      // person who signed in before creating an account - not a disclosure.
      if (cause instanceof ApiError && cause.code === 'ACCOUNT_NOT_FOUND') {
        setMode('REGISTER');
        resetChallenge();
        setError('No LocateMe account uses this yet. Add your name to create one.');
        return;
      }
      if (cause instanceof ApiError && cause.code && TERMINAL_CODES.has(cause.code)) setTerminal(true);
      setError(failureMessage(cause, 'Unable to verify the code. Check your connection and try again.'));
    } finally { setBusy(false); }
  }

  async function resend() {
    if (busy || resendSeconds || spent) return;
    setBusy(true); setError('');
    try {
      const result = await api<{ expiresAt?: string; retryAfterSeconds: number }>(`/auth/challenges/${challengeId}/resend`, {
        method: 'POST', body: '{}'
      });
      const receivedAt = Date.now();
      if (result.expiresAt) setExpiresAt(new Date(result.expiresAt).getTime());
      setResendAt(receivedAt + result.retryAfterSeconds * 1000);
      setNow(receivedAt); setCode('');
    } catch (cause) {
      if (cause instanceof ApiError && cause.code && TERMINAL_CODES.has(cause.code)) setTerminal(true);
      setError(failureMessage(cause, 'Unable to resend the code.'));
    } finally { setBusy(false); }
  }

  if (challengeId) return (
    <form className="auth-card" onSubmit={submitCode}>
      <small>VERIFY {identifierType}</small>
      <h2>Enter your code</h2>
      <p className="auth-help">Enter the six-digit code sent to your {destinationLabel}. Codes expire after five minutes.</p>
      <label>Six-digit code
        <input
          className="otp-input"
          value={code}
          onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          autoFocus
          disabled={busy || spent}
          required
        />
      </label>
      <span className="otp-countdown" aria-live="polite">
        {spent
          ? 'This code can no longer be used. Request a new code.'
          : `Code expires in ${Math.floor(expirySeconds / 60)}:${String(expirySeconds % 60).padStart(2, '0')}`}
      </span>
      {error && <div className="error-banner" role="alert">{error}</div>}
      {spent
        ? <PrimaryButton type="button" onClick={requestNewCode} disabled={busy}>{busy ? 'Requesting code…' : 'Request a new code'}</PrimaryButton>
        : <PrimaryButton type="submit" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify and continue'}</PrimaryButton>}
      <SecondaryButton type="button" onClick={resend} disabled={busy || resendSeconds > 0 || spent}>
        {resendSeconds ? `Resend in ${resendSeconds}s` : 'Resend code'}
      </SecondaryButton>
      <button className="auth-text-button" type="button" onClick={resetChallenge} disabled={busy}>Use a different {destinationLabel}</button>
    </form>
  );

  if (!capabilities) return (
    <section className="auth-card" aria-live="polite">
      <small>WELCOME BACK</small>
      <h2>Sign in</h2>
      <p className="auth-help">
        {capabilitiesBusy ? 'Checking available sign-in methods…' : capabilitiesError}
      </p>
      {!capabilitiesBusy && <PrimaryButton type="button" onClick={loadCapabilities}>Try again</PrimaryButton>}
    </section>
  );

  const availableTypes = ([
    capabilities.phone && 'PHONE',
    capabilities.email && 'EMAIL'
  ].filter(Boolean) as IdentifierType[]);

  return (
    <form className="auth-card" onSubmit={requestCode}>
      <small>{mode === 'REGISTER' ? 'CREATE ACCOUNT' : 'WELCOME BACK'}</small>
      <h2>{mode === 'REGISTER' ? 'Create your account' : 'Sign in'}</h2>
      {availableTypes.length > 1 && <div className="auth-choice" aria-label="Login identifier type">
        {availableTypes.map(type => <button
          key={type}
          type="button"
          aria-pressed={identifierType === type}
          onClick={() => { setIdentifierType(type); setIdentifier(''); setError(''); }}
        >{type === 'PHONE' ? 'Phone' : 'Email'}</button>)}
      </div>}
      {!capabilities.phone && <p className="auth-help">Phone sign-in is not available yet. Use email to continue.</p>}
      {!capabilities.email && <p className="auth-help">Email sign-in is not available right now. Use phone to continue.</p>}
      {mode === 'REGISTER' && <label>Full name<input value={name} onChange={event => setName(event.target.value)} autoComplete="name" required minLength={2} disabled={busy}/></label>}
      <label>{identifierType === 'PHONE' ? 'Phone number' : 'Email address'}
        <input
          value={identifier}
          onChange={event => setIdentifier(event.target.value)}
          type={identifierType === 'EMAIL' ? 'email' : 'tel'}
          inputMode={identifierType === 'EMAIL' ? 'email' : 'tel'}
          autoComplete={identifierType === 'EMAIL' ? 'email' : 'tel'}
          placeholder={identifierType === 'PHONE' ? '080 1234 5678' : 'you@example.com'}
          required
          disabled={busy}
        />
      </label>
      {error && <div className="error-banner" role="alert">{error}</div>}
      <PrimaryButton type="submit" disabled={busy}>{busy ? 'Requesting code…' : mode === 'REGISTER' ? 'Create account with code' : 'Send login code'}</PrimaryButton>
      <SecondaryButton type="button" onClick={() => { setMode(current => current === 'REGISTER' ? 'LOGIN' : 'REGISTER'); setError(''); }} disabled={busy}>
        {mode === 'REGISTER' ? 'I already have an account' : 'Create an account'}
      </SecondaryButton>
      <p className="privacy-copy">We use one-time codes, never sign-in links. Adults explicitly choose what they share.</p>
    </form>
  );
}
